import {
  Card,
  Col,
  Divider,
  Flex,
  Row,
  Select,
  Typography,
  Tooltip,
  Table,
  Input,
  Descriptions,
} from "antd";
import { useState, forwardRef, useEffect } from "react";
import { IHospital } from "../../../interfaces/IHospital";
import {
  DownloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  MailOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  InfoOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { errorMessage } from "../../../utils/notificationUtils";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../utils/constants";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { Modal } from "antd";
import { Badge } from "antd";
import CreditNumberModal from "./CreditNumberModal";
import ClaimIdModal from "./ClaimIdModal";
import { Button } from "antd";
import MailTimelineContent from "../../../utils/MailTimelineContent";
import { getMailTimeline } from "../../../apis/patientService";
import { getSchemeDisplayName } from "../../../utils/schemeDisplay";

const { RangePicker } = DatePicker;

type ReconciliationProps = {
  selectedHospital: IHospital | null;
  handleHospitalChange: (hospitalId: number) => void;
  availableHospitals: Array<IHospital> | undefined;
  allEmails: any;
  isDownloadingDocument: any;
  downloadingDocumentUrl: any;
  handleDownloadDocument: any;
  handleOpen: any;
  searchTerm: any;
  setSearchTerm: any;
  handleSearch: any;
  dateRange: any;
  handleDateRangeChange: any;
  handleSecondarySchemeChange: any;
  secondarySchemes: any;
  secondarySchemeSelections: any;
  emailsLoading: boolean;
  handleUpdateEmailData: any;
  fetchEmailData: any;
  currentPage: number;
  pageSize: number;
  handleFilterChange?: any;
  handleTableChange?: any;
};

const { Text, Title } = Typography;

const Email = forwardRef<any, ReconciliationProps>(
  (
    {
      selectedHospital,
      handleHospitalChange,
      availableHospitals,
      allEmails,
      isDownloadingDocument,
      downloadingDocumentUrl,
      handleOpen,
      handleDownloadDocument,
      searchTerm,
      handleSearch,
      setSearchTerm,
      dateRange,
      handleDateRangeChange,
      handleSecondarySchemeChange,
      secondarySchemeSelections,
      secondarySchemes,
      emailsLoading,
      handleUpdateEmailData,
      fetchEmailData,
      currentPage,
      pageSize,
      handleFilterChange,
      handleTableChange,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const [expandedCardIndex, setExpandedCardIndex] = useState<any>(null);
    const [showMailModal, setShowMailModal] = useState(false);
    const [creditNumberModalOpen, setCreditNumberModalOpen] = useState(false);
    const [claimIdModalOpen, setClaimIdModalOpen] = useState(false);
    const [emailData, setEmailData] = useState<any>({});
    const [mailTimelineContent, setMailTimelineContent] = useState<any>([]);

    const [mailData, setMailData] = useState<any>({
      patientId: null,
      subject: "N/A",
      body: "N/A",
      from: "N/A",
      to: "N/A",
      date: "N/A",
      attachments: [],
      patientName: null,
      cardId: null,
      beneficiaryId: null,
      policyNumber: null,
      intimatedAmount: null,
      approvedAmount: null,
      finalApprovedAmount: null,
      queryRequirements: null,
      updatedStatus: null,
      updatedBy: null,
      ccAddresses: null,
      attachmentCount: null,
      attachmentNames: null,
      classificationConfidence: null,
      classificationReasoning: null,
      ocrClassification: null,
      ocrConfidence: null,
      ocrResult: null,
    });

    const [editingId, setEditingId] = useState<any>(null);
    const [editingValue, setEditingValue] = useState<string>("");
    const [creditError, setCreditError] = useState<string>("");

    const [editingClaimId, setEditingClaimId] = useState<any>(null);
    const [editingClaimValue, setEditingClaimValue] = useState<string>("");

    enum FilterType {
      ALL = "ALL",
      PREAUTH = "PREAUTH",
      DISCHARGE = "DISCHARGE",
      CLAIM = "CLAIM",
      STACK_VIEW = "STACK_VIEW",
    }
    const [currentFilterButtonSelected, setCurrentFilterButtonSelected] =
      useState<any>(FilterType.ALL);

    useEffect(() => {
      setExpandedCardIndex(null);
      setMailTimelineContent([]);
    }, [currentPage, currentFilterButtonSelected]);

    const updateEmailData = async (id: any, reqData: any) => {
      try {
        await handleUpdateEmailData(id, reqData);
        if (reqData?.creditNumber !== null && creditNumberModalOpen) {
          setCreditNumberModalOpen(false);
          setEditingId(null);
        }
        if (reqData?.claim !== null && claimIdModalOpen) {
          setClaimIdModalOpen(false);
          setEditingId(null);
        }
      } catch (e: any) {
        setCreditNumberModalOpen(false);
        setClaimIdModalOpen(false);
      }
    };

    const handleCreditNumberChange = async (
      selectedRecord: any,
      value: string,
    ) => {
      try {
        if (!value || value.length < 3) {
          errorMessage("Credit number must be at least 3 characters.");
          return;
        }
        const reqPayload = { creditNumber: value, claimId: null };

        const response = await fetchEmailData(reqPayload);
        const emailData = {
          g7Value: response?.data,
          currentEditValue: value,
          emailValue: { ...selectedRecord },
        };
        setEmailData(emailData);
        setCreditNumberModalOpen(true);
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          "Failed to get email data. Please, Try again later.";
        errorMessage(message);
      }
    };

    const handleClaimIdChange = async (selectedRecord: any, value: string) => {
      try {
        if (!value || value.length < 3) {
          errorMessage("Claim id must be at least 3 characters.");
          return;
        }
        const reqPayload = { creditNumber: null, claimId: value };

        const response = await fetchEmailData(reqPayload);
        const emailData = {
          g7Value: response?.data,
          currentEditValue: value,
          emailValue: { ...selectedRecord },
        };
        setEmailData(emailData);
        setClaimIdModalOpen(true);
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          "Failed to get email data. Please, Try again later.";
        errorMessage(message);
      }
    };

    const handleFilterClicked = (type: FilterType) => {
      if (
        !currentFilterButtonSelected ||
        currentFilterButtonSelected !== type
      ) {
        setCurrentFilterButtonSelected(type);
        handleFilterChange(type);
      } else {
        setCurrentFilterButtonSelected(null);
        handleFilterChange(null);
      }
    };

    const getCreditNumberForRecord = (record: any) => {
      if (!record) return "-";
      return record?.creditNumber ?? "-";
    };

    const getClaimForRecord = (record: any) => {
      if (!record) return "-";
      return record?.claimId ?? "-";
    };

    const getRowKey = (record: any) => {
      if (currentFilterButtonSelected === FilterType.STACK_VIEW) {
        return `VISIT_${record.visitId}`;
      }
      return `EMAIL_${record.id}`;
    };

    const mapMSTListData = (mstList: any[]) => {
      return mstList.map((item: any) => ({
        id: item.id,
        caseId: item.case_id,
        visitId: item.visit_id,
        updatedStatus: item.updated_status,
        updatedBy: item.updated_by,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        patientName: item.patient_name,
        cardId: item.card_id,
        beneficiaryId: item.beneficiary_id,
        policyNumber: item.policy_number,
        intimatedAmount: item.intimated_amount,
        approvedAmount: item.approved_amount,
        finalApprovedAmount: item.final_approved_amount,
        queryRequirements: item.query_requirements,
        documentUrl: item.document_url,
        mailSubject: item.mail_subject,
        mailBody: item.mail_body,
        classificationReasoning: item.classification_reasoning,
        sourceFromEmail: item.source_from_email,
        sourceAttachmentName: item.source_attachment_name,
        ocrResultClassification: item.ocr_result_classification,
        creditNoteNumber: item.credit_note_number,
        schemeName: item.scheme_name,
        alternateName: item.alternate_name,
        claimId: item.claim_id,
        doa: item.doa,
        healthCareName: item.health_care_name,
        healthCareId: item.health_care_id,
        schemeId: item.scheme_id,
        ocrClassClassification: item.ocr_class_classification,
      }));
    };

    const fetchTimelineData = async (record: any) => {
      try {
        if (currentFilterButtonSelected === FilterType.STACK_VIEW) {
          if (record?.mstList?.length) {
            const timelineData = mapMSTListData(record?.mstList);
            setMailTimelineContent(timelineData);
          }
        } else {
          if (record?.visitId) {
            const res = await getMailTimeline(record?.visitId);
            setMailTimelineContent(res);
          } else {
            setMailTimelineContent([]);
          }
        }
      } catch (err) {
        setMailTimelineContent([]);
        console.error("error in mail timeline api", err);
      }
    };

    // const handleExpand = (expanded: boolean, record: any) => {
    //   if (!allEmails || !Array.isArray(allEmails)) {
    //     setExpandedCardIndex(null);
    //     return;
    //   }

    //   const idx = allEmails.findIndex((r: any) => r?.id === record?.id);
    //   setExpandedCardIndex(expanded && idx >= 0 ? idx : null);
    // };

    const handleExpand = (expanded: boolean, record: any) => {
      if (!expanded) {
        setExpandedCardIndex(null);
        return;
      }

      const key = getRowKey(record);

      setExpandedCardIndex(key);
      fetchTimelineData(record);
    };

    const secondarySchemeOptions = secondarySchemes
      ?.filter((scheme: any) => scheme && scheme.id !== undefined)
      ?.map((scheme: any) => ({
        label: getSchemeDisplayName(scheme),
        value: scheme.id as number,
      }));

    const emailColumns = [
      {
        title: <Text strong>S.NO.</Text>,
        dataIndex: "sno",
        key: "sno",
        render: (_: any, __: any, index: any) =>
          (currentPage - 1) * pageSize + index + 1,
      },
      {
        title: <Text strong>Patient Name</Text>,
        dataIndex: "patientName",
        key: "patientName",
        render: (text: string) => (
          <Tooltip title={text}>
            <Text>{text?.length > 14 ? text?.slice(0, 14) + "..." : text}</Text>
          </Tooltip>
        ),
      },
      {
        title: <Text strong>Scheme</Text>,
        dataIndex: "schemeName",
        key: "schemeName",
        render: (_: any, record: any) => {
          const schemeDisplayName = getSchemeDisplayName(record);
          return (
          <Tooltip title={schemeDisplayName}>
            <Text>{schemeDisplayName?.length > 20 ? schemeDisplayName?.slice(0, 20) + "..." : schemeDisplayName}</Text>
          </Tooltip>
        );
        },
      },
      {
        title: <Text strong>Credit Number</Text>,
        dataIndex: "creditNumber",
        key: "creditNumber",
        render: (_: any, record: any) => {
          const displayed = getCreditNumberForRecord(record);

          const isStackView =
            currentFilterButtonSelected === FilterType.STACK_VIEW;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {editingId === record.id ? (
                <>
                  <Input
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    style={{ width: 140 }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleCreditNumberChange(record, editingValue);
                      if (e.key === "Escape") {
                        setEditingId(null);
                        setEditingValue("");
                        setCreditError("");
                      }
                    }}
                  />
                  <CheckOutlined
                    style={{ cursor: "pointer", color: "#2E8B57" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreditNumberChange(record, editingValue);
                    }}
                  />
                  <CloseOutlined
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingId(null);
                      setEditingValue("");
                      setCreditError("");
                    }}
                  />
                  {creditError && (
                    <Text type="danger" style={{ marginLeft: 8 }}>
                      {creditError}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Tooltip title={displayed}>
                    <Text style={{ fontWeight: "bold" }}>
                      {displayed?.length > 8
                        ? displayed?.slice(0, 8) + "..."
                        : displayed}
                    </Text>
                  </Tooltip>
                  {!isStackView && (
                    <EditOutlined
                      style={{ cursor: "pointer", color: "#273881" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(record.id);
                        setEditingValue(
                          displayed === "-" ? "" : String(displayed),
                        );
                        setCreditError("");
                      }}
                    />
                  )}
                </>
              )}
            </div>
          );
        },
      },
      {
        title: <Text strong>Doa</Text>,
        dataIndex: "doa",
        key: "doa",
        sorter: true,
        render: (text: string) => {
          const formattedDate =
            text && text !== "-" && text !== "N/A"
              ? dayjs(text).format("DD-MM-YYYY")
              : text;
          return (
            <Tooltip title={formattedDate}>
              <Text style={{ fontWeight: "bold" }}>{formattedDate}</Text>
            </Tooltip>
          );
        },
      },
      {
        title: <Text strong>Claim Id</Text>,
        dataIndex: "claimId",
        key: "claimId",
        render: (_: any, record: any) => {
          const displayed = getClaimForRecord(record);
          const isStackView =
            currentFilterButtonSelected === FilterType.STACK_VIEW;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {editingClaimId === record.id ? (
                <>
                  <Input
                    value={editingClaimValue}
                    onChange={(e) => setEditingClaimValue(e.target.value)}
                    style={{ width: 140 }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleClaimIdChange(record, editingClaimValue);
                      if (e.key === "Escape") {
                        setEditingClaimId(null);
                        setEditingClaimValue("");
                      }
                    }}
                  />
                  <CheckOutlined
                    style={{ cursor: "pointer", color: "#2E8B57" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClaimIdChange(record, editingClaimValue);
                    }}
                  />
                  <CloseOutlined
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingClaimId(null);
                      setEditingClaimValue("");
                    }}
                  />
                </>
              ) : (
                <>
                  <Tooltip title={displayed}>
                    <Text style={{ fontWeight: "bold" }}>
                      {displayed?.length > 8
                        ? displayed?.slice(0, 8) + "..."
                        : displayed}
                    </Text>
                  </Tooltip>
                  {!isStackView && (
                    <EditOutlined
                      style={{ cursor: "pointer", color: "#273881" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingClaimId(record.id);
                        setEditingClaimValue(
                          displayed === "-" ? "" : String(displayed),
                        );
                      }}
                    />
                  )}
                </>
              )}
            </div>
          );
        },
      },
      {
        title: <Text strong>Hospital</Text>,
        dataIndex: "healthCareName",
        key: "healthCareName",
        sorter: true,
        render: (text: string) => (
          <Tooltip title={text}>
            <Text>{text?.length > 15 ? text?.slice(0, 12) + "..." : text}</Text>
          </Tooltip>
        ),
      },
      // {
      //   title: <Text strong>Mail Subject</Text>,
      //   dataIndex: "mailSubject",
      //   key: "mailSubject",
      //   render: (text: string) => (
      //     <Tooltip title={text}>
      //       <Text>{text?.length > 8 ? text?.slice(0, 8) + "..." : text}</Text>
      //     </Tooltip>
      //   ),
      // },
      // ...(currentFilterButtonSelected !== FilterType.STACK_VIEW
      //   ? [
      {
        title: <Text strong>Source</Text>,
        dataIndex: "emailSource",
        key: "emailSource",
        render: (text: string) => (
          <Tooltip title={text}>
            <Text>{text?.length > 8 ? text?.slice(0, 8) + "..." : text}</Text>
          </Tooltip>
        ),
      },
      //   ]
      // : []),
      ...(currentFilterButtonSelected !== FilterType.STACK_VIEW
        ? [
            {
              title: <Text strong>Mail</Text>,
              dataIndex: "mail",
              key: "mail",
              render: (_: any, record: any, index: any) => {
                return (
                  <div>
                    <Tooltip title="View Mail">
                      <MailOutlined
                        style={{
                          color: "#273881",
                          fontSize: "0.9rem",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();

                          const p = allEmails[index];

                          setMailData({
                            // patientId: patientId,
                            subject: p.mailSubject || "N/A",
                            body: p.mailBody || "N/A",
                            from: p.emailSource || "N/A",
                            to: p.mailReceiver || "N/A",
                            date: p.updatedAt || "-",
                            attachments: p.documentUrl || [],
                            // New fields mapped to actual API field names
                            patientName: p.patientName || null,
                            cardId: p.cardId || null,
                            beneficiaryId: p.beneficiaryId || null,
                            policyNumber: p.policyNumber || null,
                            intimatedAmount: p.intimatedAmount || null,
                            approvedAmount: p.approvedAmount || null,
                            finalApprovedAmount: p.finalApprovedAmount || null,
                            queryRequirements: p.queryRequirements || null,
                            updatedStatus: p.updatedStatus || null,
                            updatedBy: p.updatedBy || null,
                            ccAddresses: p.mailSourceCcAddresses || null,
                            attachmentCount:
                              p.mailSourceAttachmentCount || null,
                            attachmentNames: p.documentName || null,
                            classificationConfidence:
                              p.mailClassificationConfidence || null,
                            classificationReasoning:
                              p.classificationReasoning || null,
                            ocrClassification: p.ocrClassClassification || null,
                            ocrConfidence: p.mailOcrClassConfidence || null,
                            ocrResult: p.ocrResultClassification || null,
                          });

                          setShowMailModal(true);
                        }}
                      />
                    </Tooltip>
                  </div>
                );
              },
            },
          ]
        : []),
      // ...(currentFilterButtonSelected !== FilterType.STACK_VIEW
      //   ? [
      {
        title: <Text strong>Updated At</Text>,
        dataIndex: "updatedAt",
        key: "updatedAt",
        sorter: true,
        render: (_: any, record: any) => {
          return (
            <div>
              {record?.updatedAt
                ? dayjs(record?.updatedAt).format("DD-MM-YYYY HH:mm:ss")
                : "DD-MM-YYYY"}
            </div>
          );
        },
      },
      //     ]
      //   : []),
      // ...(currentFilterButtonSelected !== FilterType.STACK_VIEW
      //   ? [
      {
        title: <Text strong>Status</Text>,
        dataIndex: "updatedStatus",
        key: "updatedStatus",
        sorter: true,
        render: (updatedStatus: string) => {
          if (updatedStatus?.toUpperCase()?.includes("PENDING")) {
            return (
              <Tooltip title={updatedStatus}>
                <Text type="warning">
                  {updatedStatus?.length > 18
                    ? updatedStatus?.slice(0, 18) + "..."
                    : updatedStatus}
                </Text>
              </Tooltip>
            );
          } else if (
            updatedStatus?.toUpperCase()?.includes("COMPLETED") ||
            updatedStatus?.toUpperCase()?.includes("APPROVED")
          ) {
            return (
              <Tooltip title={updatedStatus}>
                <Text type="success">
                  {updatedStatus?.length > 18
                    ? updatedStatus?.slice(0, 18) + "..."
                    : updatedStatus}
                </Text>
              </Tooltip>
            );
          } else if (
            updatedStatus?.toUpperCase()?.includes("FAILED") ||
            updatedStatus?.toUpperCase()?.includes("REJECTED")
          ) {
            return (
              <Tooltip title={updatedStatus}>
                <Text type="danger">
                  {updatedStatus?.length > 18
                    ? updatedStatus?.slice(0, 18) + "..."
                    : updatedStatus}
                </Text>
              </Tooltip>
            );
          } else if (
            updatedStatus?.toUpperCase()?.includes("PROCESSING") ||
            updatedStatus?.toUpperCase()?.includes("IN REVIEW")
          ) {
            return (
              <Tooltip title={updatedStatus}>
                <Text style={{ color: "#1677ff" }}>
                  {updatedStatus?.length > 18
                    ? updatedStatus?.slice(0, 18) + "..."
                    : updatedStatus}
                </Text>
              </Tooltip>
            );
          } else {
            return (
              <Tooltip title={updatedStatus}>
                <Text type="secondary">
                  {updatedStatus?.length > 18
                    ? updatedStatus?.slice(0, 18) + "..."
                    : updatedStatus}
                </Text>
              </Tooltip>
            );
          }
        },
      },
      //   ]
      // : []),
      // ...(currentFilterButtonSelected !== FilterType.STACK_VIEW
      //   ? [
      //       {
      //         title: <Text strong>Updated By</Text>,
      //         dataIndex: "updatedBy",
      //         key: "updatedBy",
      //         render: (text: string) => (
      //           <Tooltip title={text}>
      //             <Text>
      //               {text?.length > 14 ? text?.slice(0, 14) + "..." : text}
      //             </Text>
      //           </Tooltip>
      //         ),
      //       },
      //     ]
      //   : []),
      // ...(currentFilterButtonSelected === FilterType.STACK_VIEW
      //   ? [
      //       {
      //         title: <Text strong>Timeline</Text>,
      //         dataIndex: "timeline",
      //         key: "timeline",
      //         render: (text: string, record: any) => (
      //           <Tooltip title="Stack View" color="blue">
      //             <DatabaseOutlined
      //               style={{
      //                 color: "#273881",
      //                 fontSize: "0.9rem",
      //                 cursor: "pointer",
      //               }}
      //               onClick={(e) => {
      //                 e.stopPropagation();
      //                 setStackViewVisitEmails(record?.mstList);
      //               }}
      //             />
      //           </Tooltip>
      //         ),
      //       },
      //     ]
      //   : []),
    ];

    const expandedRowRender = (record: any) => {
      const emailId = expandedCardIndex;

      return (
        <div
          className="expanded-content"
          style={{
            backgroundColor: "#f2f2f2",
            borderRadius: "8px",
          }}
        >
          <Row gutter={[12, 8]} style={{ padding: "12px" }} align="middle">
            <Col xs={24} sm={8} lg={5}>
              <Title
                level={5}
                style={{ margin: 0, color: "#1677ff", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (record?.visitId) {
                    navigate(`${ROUTES.ALL_VISIT}/${record?.visitId}`);
                  }
                }}
              >
                {record?.patientName?.toUpperCase()}
              </Title>
              <Text strong>Card Id:</Text> <Text>{record?.cardId}</Text>
              <br />
              {/* <Text strong>Beneficiary Id:</Text>{" "} */}
              {/* <Text>{record?.beneficiaryId || "-"}</Text> */}
            </Col>

            <Col>
              <Divider type="vertical" style={{ height: "100%" }} />
            </Col>

            <Col xs={24} sm={16} lg={18}>
              <Row gutter={[16, 6]} align="middle">
                <Col span={6}>
                  <Text strong>Status:</Text>{" "}
                  <Text>{record?.updatedStatus}</Text>
                </Col>

                <Col span={6}>
                  <Text strong>Hospital:</Text>{" "}
                  <Text>{record?.healthCareName}</Text>
                </Col>

                <Col span={6}>
                  <Text strong>Policy Number:</Text>{" "}
                  <Text>{record?.policyNumber || "-"}</Text>
                </Col>

                <Col span={6}>
                  <Text strong>Scheme:</Text>{" "}
                  <Text>{getSchemeDisplayName(record)?.toUpperCase()}</Text>
                </Col>

                <Col span={6}>
                  <Text strong>Credit Number:</Text>{" "}
                  <Text>{getCreditNumberForRecord(record)}</Text>
                </Col>

                <Col span={6}>
                  <Text strong>Claim Id:</Text>{" "}
                  <Text>{getClaimForRecord(record)}</Text>
                </Col>

                {record?.documentUrl && (
                  <Col span={6}>
                    <Text strong>Mail Document:</Text>
                    <span style={{ marginLeft: 8 }}>
                      <Tooltip title="View">
                        <EyeOutlined
                          style={{
                            color: "#273881",
                            cursor: "pointer",
                            marginRight: 8,
                          }}
                          onClick={() =>
                            handleOpen(
                              record?.id ?? record?.visitId ?? 0,
                              "Mail",
                              record?.documentUrl,
                            )
                          }
                        />
                      </Tooltip>

                      {isDownloadingDocument &&
                      downloadingDocumentUrl === record?.documentUrl ? (
                        <LoadingOutlined />
                      ) : (
                        <Tooltip title="Download">
                          <DownloadOutlined
                            style={{ color: "#273881", cursor: "pointer" }}
                            onClick={() =>
                              handleDownloadDocument &&
                              handleDownloadDocument(
                                record?.documentName,
                                record?.documentUrl,
                              )
                            }
                          />
                        </Tooltip>
                      )}
                    </span>
                  </Col>
                )}

                {record?.mailBody && (
                  <Col span={6}>
                    <Text strong>Mail:</Text>
                    <span style={{ marginLeft: 8 }}>
                      <Tooltip title="View Mail">
                        <MailOutlined
                          style={{ color: "#273881", cursor: "pointer" }}
                          onClick={() => {
                            const p = record;
                            setMailData({
                              subject: p.mailSubject || "N/A",
                              body: p.mailBody || "N/A",
                              from: p.emailSource || "N/A",
                              to: p.mailReceiver || "N/A",
                              date: p.updatedAt || "-",
                              attachments: p.documentUrl || [],
                              patientName: p.patientName || null,
                              cardId: p.cardId || null,
                              beneficiaryId: p.beneficiaryId || null,
                              policyNumber: p.policyNumber || null,
                              intimatedAmount: p.intimatedAmount || null,
                              approvedAmount: p.approvedAmount || null,
                              finalApprovedAmount:
                                p.finalApprovedAmount || null,
                              queryRequirements: p.queryRequirements || null,
                              updatedStatus: p.updatedStatus || null,
                              updatedBy: p.updatedBy || null,
                              ccAddresses: p.mailSourceCcAddresses || null,
                              attachmentCount:
                                p.mailSourceAttachmentCount || null,
                              attachmentNames: p.documentName || null,
                              classificationConfidence:
                                p.mailClassificationConfidence || null,
                              classificationReasoning:
                                p.classificationReasoning || null,
                              ocrClassification:
                                p.ocrClassClassification || null,
                              ocrConfidence: p.mailOcrClassConfidence || null,
                              ocrResult: p.ocrResultClassification || null,
                            });
                            setShowMailModal(true);
                          }}
                        />
                      </Tooltip>
                    </span>
                  </Col>
                )}

                {/* <Col span={6}>
                  <Text strong>Timeline:</Text>
                  <span style={{ marginLeft: 8 }}>
                    <Tooltip title="View">
                      <EyeOutlined
                        style={{ color: "#273881", cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            currentFilterButtonSelected ===
                            FilterType.STACK_VIEW
                          ) {
                            setStackViewVisitEmails(record?.mstList);
                          } else if (record?.visitId) {
                            fetchTimeline(record?.visitId);
                          } else {
                            errorMessage("Email is not linked to any visit");
                          }
                        }}
                      />
                    </Tooltip>
                  </span>
                </Col> */}
              </Row>
            </Col>
          </Row>

          {mailTimelineContent?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <MailTimelineContent
                data={mailTimelineContent}
                handleOpen={handleOpen}
                handleDownloadDocument={handleDownloadDocument}
                isDownloadingDocument={isDownloadingDocument}
                downloadingDocumentUrl={downloadingDocumentUrl}
              />
            </div>
          )}
        </div>
      );
    };

    const filterOptionFunction = (
      input: string,
      option?: { label: string; value: number },
    ) => {
      return (
        option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
      );
    };

    return (
      <div>
        <Card style={{ marginBottom: 16 }} bordered>
          <Flex
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            style={{ width: "100%", gap: "16px" }}
          >
            <Flex gap={16} wrap="wrap">
              <div style={{ width: 250 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  HOSPITAL
                </Text>
                <Select
                  showSearch
                  placeholder="Select a Hospital"
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  value={selectedHospital?.value}
                  options={availableHospitals?.map((hospital) => ({
                    label: hospital.label,
                    value: hospital.value,
                  }))}
                  onChange={handleHospitalChange}
                />
              </div>

              <div style={{ width: 250 }}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Scheme
                </Text>
                <Select
                  value={secondarySchemeSelections}
                  mode="multiple"
                  style={{ width: "100%" }}
                  showSearch
                  optionFilterProp="label"
                  filterOption={filterOptionFunction}
                  maxTagCount={1}
                  maxTagPlaceholder={(omittedValues) => (
                    <span
                      style={{ display: "inline-flex", alignItems: "center" }}
                    >
                      +{omittedValues.length}
                    </span>
                  )}
                  onChange={handleSecondarySchemeChange}
                  placeholder="SELECT TPA SCHEME"
                  allowClear
                  options={secondarySchemeOptions}
                />
              </div>

              <div style={{ marginTop: "2rem" }}>
                <Button
                  style={{
                    backgroundColor:
                      currentFilterButtonSelected === FilterType.ALL
                        ? "rgb(0, 76, 143)"
                        : "white",
                    color:
                      currentFilterButtonSelected === FilterType.ALL
                        ? "white"
                        : "rgb(0, 76, 143)",
                    border: "1px solid rgb(0, 76, 143)",
                    boxShadow: "0px 2px 8px 0px #00000040",
                  }}
                  onClick={() => handleFilterClicked(FilterType.ALL)}
                >
                  All
                </Button>
              </div>

              <div style={{ marginTop: "2rem" }}>
                <Button
                  style={{
                    backgroundColor:
                      currentFilterButtonSelected === FilterType.PREAUTH
                        ? "rgb(0, 76, 143)"
                        : "white",
                    color:
                      currentFilterButtonSelected === FilterType.PREAUTH
                        ? "white"
                        : "rgb(0, 76, 143)",
                    border: "1px solid rgb(0, 76, 143)",
                    boxShadow: "0px 2px 8px 0px #00000040",
                  }}
                  onClick={() => handleFilterClicked(FilterType.PREAUTH)}
                >
                  PreAuth
                </Button>
              </div>

              <div style={{ marginTop: "2rem" }}>
                <Button
                  style={{
                    backgroundColor:
                      currentFilterButtonSelected === FilterType.DISCHARGE
                        ? "rgb(0, 76, 143)"
                        : "white",
                    color:
                      currentFilterButtonSelected === FilterType.DISCHARGE
                        ? "white"
                        : "rgb(0, 76, 143)",
                    border: "1px solid rgb(0, 76, 143)",
                    boxShadow: "0px 2px 8px 0px #00000040",
                  }}
                  onClick={() => handleFilterClicked(FilterType.DISCHARGE)}
                >
                  Discharge
                </Button>
              </div>

              <div style={{ marginTop: "2rem" }}>
                <Button
                  style={{
                    backgroundColor:
                      currentFilterButtonSelected === FilterType.CLAIM
                        ? "rgb(0, 76, 143)"
                        : "white",
                    color:
                      currentFilterButtonSelected === FilterType.CLAIM
                        ? "white"
                        : "rgb(0, 76, 143)",
                    border: "1px solid rgb(0, 76, 143)",
                    boxShadow: "0px 2px 8px 0px #00000040",
                  }}
                  onClick={() => handleFilterClicked(FilterType.CLAIM)}
                >
                  Claim
                </Button>
              </div>

              <div style={{ marginTop: "2rem" }}>
                <Button
                  style={{
                    backgroundColor:
                      currentFilterButtonSelected === FilterType.STACK_VIEW
                        ? "rgb(0, 76, 143)"
                        : "white",
                    color:
                      currentFilterButtonSelected === FilterType.STACK_VIEW
                        ? "white"
                        : "rgb(0, 76, 143)",
                    border: "1px solid rgb(0, 76, 143)",
                    boxShadow: "0px 2px 8px 0px #00000040",
                  }}
                  onClick={() => handleFilterClicked(FilterType.STACK_VIEW)}
                >
                  STACK VIEW
                </Button>
              </div>
            </Flex>

            <Flex
              gap={12}
              align="center"
              wrap="wrap"
              style={{ marginTop: "2rem" }}
            >
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="DD-MM-YYYY"
                style={{ border: "0.4px solid #222222", width: 230 }}
                allowClear
              />

              <Button
                // type="primary"
                style={{
                  borderColor: "rgb(0, 76, 143)",
                  color: "rgb(0, 76, 143)",
                  boxShadow: "0px 2px 8px 0px #00000040",
                }}
                onClick={() => handleDateRangeChange(null)}
              >
                Clear Date Range
              </Button>

              <Flex gap={6} align="center">
                <Input
                  className="custom-placeholder"
                  placeholder="Type to Search"
                  value={searchTerm || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    if (value.trim() === "") handleSearch(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                    }
                  }}
                  suffix={<SearchOutlined onClick={() => handleSearch()} />}
                  style={{ height: "30px", border: "1px solid #808184" }}
                />

                <Tooltip
                  title="Search by Name, Claim no., Credit Number"
                  placement="topLeft"
                >
                  <div
                    style={{
                      background: "#D51E1E",
                      borderRadius: "50%",
                      padding: "0px 4px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <InfoOutlined style={{ color: "white" }} />
                  </div>
                </Tooltip>
              </Flex>
            </Flex>
          </Flex>
        </Card>

        <div className="sticky-table-wrapper">
          <Table
            className="email-table"
            dataSource={allEmails}
            columns={emailColumns}
            bordered={false}
            loading={emailsLoading}
            rowKey={(record) => getRowKey(record)}
            pagination={false}
            style={{
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "ant-table-row-light" : ""
            }
            expandable={{
              expandedRowRender,
              expandedRowKeys:
                expandedCardIndex !== null ? [expandedCardIndex] : [],
              expandRowByClick: true,
              onExpand: handleExpand,
              rowExpandable: (record: any) => true,
              expandIcon: () => null,
            }}
            onRow={(record) => ({
              style: {
                backgroundColor: record.colorCodingBgHexCode || "inherit",
                cursor: "pointer",
              },
            })}
            rowHoverable={false}
            onChange={handleTableChange}
          />
        </div>

        <Modal
          open={showMailModal}
          onCancel={() => setShowMailModal(false)}
          footer={null}
          width={3000}
          centered
          bodyStyle={{
            marginTop: "20px",
            marginBottom: "20px",
            padding: "0px",
            background: "#f5f7fa",
            borderRadius: 12,
            // overflow: "hidden",
            overflowY: "scroll",
            maxHeight: "132.5vh",
          }}
        >
          {/* HEADER */}
          <div
            style={{
              background: "rgb(0, 84, 157)",
              padding: "24px 32px",
              color: "#fff",
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 600,
                marginBottom: 8,
              }}
            >
              {mailData.subject}
            </h2>
            {mailData.updatedStatus && (
              <Badge
                status={
                  mailData.updatedStatus === "APPROVED"
                    ? "success"
                    : mailData.updatedStatus === "QUERY"
                      ? "warning"
                      : "error"
                }
                text={
                  <span style={{ color: "#fff", fontWeight: 500 }}>
                    {mailData.updatedStatus}
                  </span>
                }
              />
            )}
          </div>

          {/* SCROLLABLE CONTENT */}
          <div
          // style={{ maxHeight: "calc(85vh - 100px)", overflowY: "auto" }}
          >
            {/* MAIL INFO */}
            <div
              style={{
                background: "#fff",
                padding: "20px 32px",
                margin: "16px",
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: 16,
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#273881",
                }}
              >
                Email Details
              </h3>
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="From" span={2}>
                  {mailData.from}
                </Descriptions.Item>
                <Descriptions.Item label="To" span={2}>
                  {mailData.to}
                </Descriptions.Item>
                {mailData.ccAddresses && (
                  <Descriptions.Item label="CC" span={2}>
                    {mailData.ccAddresses}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Date" span={2}>
                  {mailData.date &&
                  mailData.date !== "-" &&
                  mailData.date !== "N/A"
                    ? dayjs(mailData.date).format("DD-MM-YYYY HH:mm:ss")
                    : mailData.date}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* PATIENT & POLICY INFO */}
            {(mailData.patientName ||
              mailData.cardId ||
              mailData.beneficiaryId ||
              mailData.policyNumber) && (
              <div
                style={{
                  background: "#fff",
                  padding: "20px 32px",
                  margin: "16px",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: 16,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#273881",
                  }}
                >
                  Patient & Policy Information
                </h3>
                <Descriptions column={2} size="small" bordered>
                  {mailData.patientName && (
                    <Descriptions.Item label="Patient Name">
                      {mailData.patientName}
                    </Descriptions.Item>
                  )}
                  {mailData.cardId && (
                    <Descriptions.Item label="Card ID">
                      {mailData.cardId}
                    </Descriptions.Item>
                  )}
                  {mailData.beneficiaryId && (
                    <Descriptions.Item label="Beneficiary ID">
                      {mailData.beneficiaryId}
                    </Descriptions.Item>
                  )}
                  {mailData.policyNumber && (
                    <Descriptions.Item label="Policy Number">
                      {mailData.policyNumber}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}

            {/* CLAIM AMOUNTS */}
            {(mailData.intimatedAmount ||
              mailData.approvedAmount ||
              mailData.finalApprovedAmount) && (
              <div
                style={{
                  background: "#fff",
                  padding: "20px 32px",
                  margin: "16px",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: 16,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#273881",
                  }}
                >
                  Claim Amounts
                </h3>
                <Descriptions column={3} size="small" bordered>
                  {mailData.intimatedAmount && (
                    <Descriptions.Item label="Intimated Amount">
                      <span style={{ color: "#2E8B57", fontWeight: 600 }}>
                        ₹ {parseFloat(mailData.intimatedAmount).toFixed(2)}
                      </span>
                    </Descriptions.Item>
                  )}
                  {mailData.approvedAmount && (
                    <Descriptions.Item label="Approved Amount">
                      <span style={{ color: "#2E8B57", fontWeight: 600 }}>
                        ₹ {parseFloat(mailData.approvedAmount).toFixed(2)}
                      </span>
                    </Descriptions.Item>
                  )}
                  {mailData.finalApprovedAmount && (
                    <Descriptions.Item label="Final Approved Amount">
                      <span style={{ color: "#2E8B57", fontWeight: 600 }}>
                        ₹ {parseFloat(mailData.finalApprovedAmount).toFixed(2)}
                      </span>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}

            {/* AI CLASSIFICATION */}
            {(mailData.classificationConfidence ||
              mailData.classificationReasoning ||
              mailData.ocrClassification) && (
              <div
                style={{
                  background: "#fff",
                  padding: "20px 32px",
                  margin: "16px",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: 16,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#273881",
                  }}
                >
                  AI Analysis
                </h3>
                <Descriptions column={2} size="small" bordered>
                  {mailData.classificationConfidence && (
                    <Descriptions.Item label="Classification Confidence">
                      <span
                        style={{
                          color:
                            mailData.classificationConfidence > 0.8
                              ? "#2E8B57"
                              : mailData.classificationConfidence > 0.6
                                ? "#FFA500"
                                : "#D51E1E",
                          fontWeight: 600,
                        }}
                      >
                        {(mailData.classificationConfidence * 100).toFixed(1)}%
                      </span>
                    </Descriptions.Item>
                  )}
                  {mailData.ocrClassification && (
                    <Descriptions.Item label="OCR Classification">
                      <span style={{ fontWeight: 600 }}>
                        {mailData.ocrClassification}
                      </span>
                    </Descriptions.Item>
                  )}
                  {mailData.ocrConfidence && (
                    <Descriptions.Item label="OCR Confidence">
                      <span
                        style={{
                          color:
                            mailData.ocrConfidence > 0.8
                              ? "#2E8B57"
                              : mailData.ocrConfidence > 0.6
                                ? "#FFA500"
                                : "#D51E1E",
                          fontWeight: 600,
                        }}
                      >
                        {(mailData.ocrConfidence * 100).toFixed(1)}%
                      </span>
                    </Descriptions.Item>
                  )}
                  {mailData.ocrResult && (
                    <Descriptions.Item label="OCR Result">
                      <span style={{ fontWeight: 600 }}>
                        {mailData.ocrResult}
                      </span>
                    </Descriptions.Item>
                  )}
                  {mailData.classificationReasoning && (
                    <Descriptions.Item label="AI Reasoning" span={2}>
                      <div
                        style={{
                          padding: "12px",
                          background: "#e6f7ff",
                          borderRadius: 6,
                          borderLeft: "3px solid #1677ff",
                          fontSize: 14,
                        }}
                      >
                        {mailData.classificationReasoning}
                      </div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}

            {/* QUERY REQUIREMENTS */}
            {mailData.queryRequirements && (
              <div
                style={{
                  background: "#fff",
                  padding: "20px 32px",
                  margin: "16px",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: 16,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#273881",
                  }}
                >
                  Query Requirements
                </h3>
                <div
                  style={{
                    padding: "16px",
                    background: "#fffbe6",
                    borderRadius: 6,
                    borderLeft: "3px solid #FFA500",
                    fontSize: 14,
                    lineHeight: 1.6,
                  }}
                >
                  {mailData.queryRequirements}
                </div>
              </div>
            )}

            {/* BODY */}
            <div
              style={{
                background: "#fff",
                padding: "20px 32px",
                margin: "16px",
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: 16,
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#273881",
                }}
              >
                Message Body
              </h3>
              <div
                style={{
                  padding: "16px",
                  background: "#fafafa",
                  borderRadius: 6,
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: "#333",
                  minHeight: 120,
                }}
                dangerouslySetInnerHTML={{ __html: mailData.body }}
              />
            </div>

            {/* ATTACHMENTS - Keep as is */}
            {mailData.attachments?.length > 0 && (
              <div
                style={{
                  background: "#fff",
                  padding: "20px 32px",
                  margin: "16px",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: 16,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "#273881",
                  }}
                >
                  Attachments
                </h3>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "14px 18px",
                    border: "1px solid #e8e8e8",
                    borderRadius: 8,
                    background: "#fafafa",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f0f0f0";
                    e.currentTarget.style.borderColor = "#d9d9d9";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#fafafa";
                    e.currentTarget.style.borderColor = "#e8e8e8";
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>
                    Mail Document
                  </div>

                  <div style={{ display: "flex", gap: 20 }}>
                    <Tooltip title="View">
                      <EyeOutlined
                        style={{
                          cursor: "pointer",
                          fontSize: 18,
                          color: "#273881",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#1677ff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#273881";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                        onClick={() => {
                          setShowMailModal(false);
                          handleOpen(
                            mailData.patientId,
                            "Mail",
                            mailData?.attachments,
                          );
                        }}
                      />
                    </Tooltip>

                    <Tooltip title="Download">
                      <DownloadOutlined
                        style={{
                          cursor: "pointer",
                          fontSize: 18,
                          color: "#273881",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#1677ff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#273881";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                        onClick={() =>
                          handleDownloadDocument(
                            mailData?.attachmentNames,
                            mailData.attachments,
                          )
                        }
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}

            {/* UPDATE INFO */}
            {(mailData.updatedBy || mailData.date) && (
              <div
                style={{
                  background: "#fff",
                  padding: "16px 32px",
                  margin: "16px",
                  marginBottom: "24px",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    color: "#8c8c8c",
                  }}
                >
                  {/* {mailData.updatedBy && (
                          <span>Updated by: <strong>{mailData.updatedBy}</strong></span>
                        )} */}
                  {mailData.date &&
                    mailData.date !== "-" &&
                    mailData.date !== "N/A" && (
                      <span>
                        Last updated:{" "}
                        {dayjs(mailData.date).format("DD-MM-YYYY HH:mm:ss")}
                      </span>
                    )}
                </div>
              </div>
            )}
          </div>
        </Modal>

        <CreditNumberModal
          open={creditNumberModalOpen}
          onCancel={() => setCreditNumberModalOpen(false)}
          emailData={emailData}
          onYesClicked={updateEmailData}
        />

        <ClaimIdModal
          open={claimIdModalOpen}
          onCancel={() => setClaimIdModalOpen(false)}
          emailData={emailData}
          onYesClicked={updateEmailData}
        />
      </div>
    );
  },
);

export default Email;
